import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Button, Chip, CircularProgress, Alert,
  Tabs, Tab, Grid, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Checkbox, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InfoIcon from '@mui/icons-material/Info';
import EscolaIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import ChecklistIcon from '@mui/icons-material/Checklist';
import GetAppIcon from '@mui/icons-material/GetApp';
import QuizIcon from '@mui/icons-material/Quiz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCurso, listarSessoes, criarSessao, atualizarSessao, deletarSessao,
  listarMateriais, criarMaterial, atualizarMaterial, deletarMaterial,
  listarInscricoes, cancelarInscricao,
  listarPresencas, registrarPresencas,
  adicionarLider, removerLider, listarMinisterios
} from '../../api/cursos';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContextNavBar from '../../components/ContextNavBar';
import ModuleHeader from '../../components/ModuleHeader';
import api from '../../axios';
import InscricaoModal from './InscricaoModal';

const STATUS_COLOR = {
  rascunho: 'default', aberto: 'success', andamento: 'info',
  concluido: 'default', cancelado: 'error'
};
const STATUS_LABEL = {
  rascunho: 'Rascunho', aberto: 'Aberto', andamento: 'Em Andamento',
  concluido: 'Concluído', cancelado: 'Cancelado'
};

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const sessaoVazia = {
  titulo: '', descricao: '', data: '', hora_inicio: '', hora_fim: '',
  tipo: 'presencial', local: '', link_transmissao: '', carga_horaria: '', ordem: '',
  instrutor_id: ''
};

function ModalSessao({ open, onClose, cursoId, sessao, onSuccess }) {
  const [form, setForm] = useState(sessaoVazia);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [instrutores, setInstrutores] = useState([]);
  const editId = sessao?.id || null;

  useEffect(() => {
    if (open) {
      setForm(sessao ? {
        titulo: sessao.titulo || '',
        descricao: sessao.descricao || '',
        data: sessao.data || '',
        hora_inicio: sessao.hora_inicio || '',
        hora_fim: sessao.hora_fim || '',
        tipo: sessao.tipo || 'presencial',
        local: sessao.local || '',
        link_transmissao: sessao.link_transmissao || '',
        carga_horaria: sessao.carga_horaria != null ? String(sessao.carga_horaria) : '',
        ordem: sessao.ordem != null ? String(sessao.ordem) : '',
        instrutor_id: sessao.instrutor_id != null ? String(sessao.instrutor_id) : ''
      } : sessaoVazia);
      setErro('');
      api.get('/instrutores/').then(res => setInstrutores(res.data || [])).catch(() => {});
    }
  }, [open, sessao]);

  const handleChange = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const salvar = async () => {
    if (!form.titulo.trim()) { setErro('Título é obrigatório.'); return; }
    setSaving(true);
    setErro('');
    try {
      const payload = {
        ...form,
        carga_horaria: form.carga_horaria ? parseInt(form.carga_horaria, 10) : null,
        ordem: form.ordem ? parseInt(form.ordem, 10) : null,
        data: form.data || null,
        instrutor_id: form.instrutor_id ? parseInt(form.instrutor_id, 10) : null
      };
      if (editId) await atualizarSessao(editId, payload);
      else await criarSessao(cursoId, payload);
      onSuccess();
      onClose();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar sessão');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editId ? 'Editar Sessão' : 'Nova Sessão'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {erro && <Alert severity="error">{erro}</Alert>}
        <TextField label="Título *" value={form.titulo} onChange={handleChange('titulo')} fullWidth />
        <TextField label="Descrição" value={form.descricao} onChange={handleChange('descricao')} fullWidth multiline rows={2} />
        <TextField label="Data" type="date" value={form.data} onChange={handleChange('data')} fullWidth InputLabelProps={{ shrink: true }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Hora Início" type="time" value={form.hora_inicio} onChange={handleChange('hora_inicio')} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Hora Fim" type="time" value={form.hora_fim} onChange={handleChange('hora_fim')} fullWidth InputLabelProps={{ shrink: true }} />
        </Box>
        <FormControl fullWidth>
          <InputLabel>Tipo</InputLabel>
          <Select value={form.tipo} label="Tipo" onChange={handleChange('tipo')}>
            <MenuItem value="presencial">Presencial</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="hibrido">Híbrido</MenuItem>
          </Select>
        </FormControl>
        <TextField label="Local" value={form.local} onChange={handleChange('local')} fullWidth />
        <FormControl fullWidth size="small">
          <InputLabel>Instrutor</InputLabel>
          <Select value={form.instrutor_id || ''} label="Instrutor"
            onChange={e => setForm(f => ({ ...f, instrutor_id: e.target.value }))}>
            <MenuItem value="">Sem instrutor</MenuItem>
            {instrutores.map(p => (
              <MenuItem key={p.id} value={String(p.id)}>{p.nome}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label="Link de Transmissão" value={form.link_transmissao} onChange={handleChange('link_transmissao')} fullWidth />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Carga Horária (min)" type="number" value={form.carga_horaria} onChange={handleChange('carga_horaria')} fullWidth inputProps={{ min: 0 }} />
          <TextField label="Ordem" type="number" value={form.ordem} onChange={handleChange('ordem')} fullWidth inputProps={{ min: 0 }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={salvar} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getYoutubeId(url) {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

const materialVazio = { titulo: '', tipo: 'texto', conteudo: '' };

function ModalMaterial({ open, onClose, sessaoId, material, onSuccess }) {
  const [form, setForm] = useState(materialVazio);
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const editId = material?.id || null;

  useEffect(() => {
    if (open) {
      if (material) {
        let parsed = {};
        try { parsed = JSON.parse(material.conteudo || '{}'); } catch {}
        setForm({
          titulo: material.titulo || '',
          tipo: material.tipo || 'texto',
          conteudo: material.conteudo || ''
        });
        setFields(parsed);
      } else {
        setForm(materialVazio);
        setFields({});
      }
      setErro('');
    }
  }, [open, material]);

  const handleChange = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const handleTipoChange = (e) => {
    setForm(f => ({ ...f, tipo: e.target.value }));
    setFields({});
  };

  const handleFieldChange = (field) => (e) => {
    setFields(f => ({ ...f, [field]: e.target.value }));
  };

  const serializeConteudo = () => {
    switch (form.tipo) {
      case 'texto': return JSON.stringify({ texto: fields.texto || '' });
      case 'imagem': return JSON.stringify({ url: fields.url || '', alt: fields.alt || '' });
      case 'link': return JSON.stringify({ url: fields.url || '', texto: fields.texto_link || '' });
      case 'video_youtube': {
        const id = getYoutubeId(fields.url || '');
        return JSON.stringify({ url: fields.url || '', id: id || '' });
      }
      case 'arquivo': return JSON.stringify({ nome: fields.nome || '', url: fields.url || '' });
      default: return '{}';
    }
  };

  const salvar = async () => {
    if (!form.titulo.trim()) { setErro('Título é obrigatório.'); return; }
    setSaving(true);
    setErro('');
    try {
      const payload = { titulo: form.titulo, tipo: form.tipo, conteudo: serializeConteudo() };
      if (editId) await atualizarMaterial(editId, payload);
      else await criarMaterial(sessaoId, payload);
      onSuccess();
      onClose();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar material');
    }
    setSaving(false);
  };

  const youtubeId = form.tipo === 'video_youtube' ? getYoutubeId(fields.url || '') : null;
  const previewUrl = form.tipo === 'imagem' ? (fields.url || '') : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editId ? 'Editar Material Didático' : 'Adicionar Material Didático'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {erro && <Alert severity="error">{erro}</Alert>}
        <TextField label="Título *" value={form.titulo} onChange={handleChange('titulo')} fullWidth />
        <FormControl fullWidth>
          <InputLabel>Tipo</InputLabel>
          <Select value={form.tipo} label="Tipo" onChange={handleTipoChange}>
            <MenuItem value="texto">Texto</MenuItem>
            <MenuItem value="imagem">Imagem</MenuItem>
            <MenuItem value="link">Link</MenuItem>
            <MenuItem value="video_youtube">Vídeo YouTube</MenuItem>
            <MenuItem value="arquivo">Arquivo</MenuItem>
          </Select>
        </FormControl>

        {form.tipo === 'texto' && (
          <TextField label="Conteúdo" value={fields.texto || ''} onChange={handleFieldChange('texto')} fullWidth multiline rows={5} />
        )}

        {form.tipo === 'imagem' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField label="URL da Imagem" value={fields.url || ''} onChange={handleFieldChange('url')} fullWidth placeholder="https://..." />
            <TextField label="Texto Alternativo" value={fields.alt || ''} onChange={handleFieldChange('alt')} fullWidth />
            {previewUrl && (
              <Box component="img" src={previewUrl} sx={{ maxHeight: 200, borderRadius: 1, mt: 1, objectFit: 'contain', bgcolor: 'grey.100' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </Box>
        )}

        {form.tipo === 'link' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField label="URL" value={fields.url || ''} onChange={handleFieldChange('url')} fullWidth placeholder="https://..." />
            <TextField label="Texto do Link" value={fields.texto_link || ''} onChange={handleFieldChange('texto_link')} fullWidth />
          </Box>
        )}

        {form.tipo === 'video_youtube' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField label="URL do YouTube" value={fields.url || ''} onChange={handleFieldChange('url')} fullWidth placeholder="https://youtube.com/watch?v=..." />
            {youtubeId && (
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${youtubeId}`}
                sx={{ width: '100%', height: 240, borderRadius: 1, mt: 1, border: 'none' }}
                allowFullScreen
              />
            )}
          </Box>
        )}

        {form.tipo === 'arquivo' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField label="Nome do Arquivo" value={fields.nome || ''} onChange={handleFieldChange('nome')} fullWidth placeholder="exemplo.pdf" />
            <TextField label="URL do Arquivo" value={fields.url || ''} onChange={handleFieldChange('url')} fullWidth placeholder="https://..." />
            {editId && (
              <Button variant="outlined" component="label" fullWidth>
                Selecionar Arquivo
                <input type="file" hidden onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const formData = new FormData();
                  formData.append('file', file);
                  try {
                    const res = await api.post(`/materiais/${editId}/upload`, formData);
                    setFields(f => ({ ...f, url: res.data.url, nome: file.name }));
                  } catch { alert('Erro ao fazer upload'); }
                }} />
              </Button>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={salvar} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} /> : (editId ? 'Salvar' : 'Adicionar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TabQuiz({ cursoId, sessoes }) {
  const [sessaoSel, setSessaoSel] = useState('');
  const [perguntas, setPerguntas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ pergunta: '', opcao_a: '', opcao_b: '', opcao_c: '', opcao_d: '', resposta_correta: 'a' });

  const carregar = async () => {
    if (!sessaoSel) return;
    setLoading(true);
    try {
      const res = await api.get(`/sessoes/${sessaoSel}/quiz`);
      setPerguntas(res.data || []);
    } catch { setErro('Erro ao carregar quiz'); }
    setLoading(false);
  };

  useEffect(() => { carregar(); }, [sessaoSel]);

  const abrirNova = () => {
    setEditando(null);
    setForm({ pergunta: '', opcao_a: '', opcao_b: '', opcao_c: '', opcao_d: '', resposta_correta: 'a' });
    setOpen(true);
  };

  const abrirEditar = (p) => {
    setEditando(p);
    setForm({ pergunta: p.pergunta, opcao_a: p.opcao_a, opcao_b: p.opcao_b, opcao_c: p.opcao_c, opcao_d: p.opcao_d, resposta_correta: p.resposta_correta || 'a' });
    setOpen(true);
  };

  const salvar = async () => {
    if (!form.pergunta.trim()) return;
    try {
      if (editando) await api.put(`/sessoes/quiz/${editando.id}`, form);
      else await api.post(`/sessoes/${sessaoSel}/quiz`, form);
      setOpen(false);
      carregar();
    } catch (e) { alert(e.response?.data?.detail || 'Erro'); }
  };

  const deletar = async (id) => {
    if (!window.confirm('Deletar pergunta?')) return;
    try { await api.delete(`/sessoes/quiz/${id}`); carregar(); }
    catch { alert('Erro'); }
  };

  const OPCOES = ['a', 'b', 'c', 'd'];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 250 }} size="small">
          <InputLabel>Sessão</InputLabel>
          <Select value={sessaoSel} label="Sessão" onChange={e => setSessaoSel(e.target.value)}>
            <MenuItem value="">Selecione uma sessão</MenuItem>
            {sessoes.map(s => <MenuItem key={s.id} value={s.id}>{s.titulo}</MenuItem>)}
          </Select>
        </FormControl>
        {sessaoSel && <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNova}>Nova Pergunta</Button>}
      </Box>

      {!sessaoSel && <Alert severity="info">Selecione uma sessão para gerenciar o quiz.</Alert>}
      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
      {loading && <CircularProgress />}

      {perguntas.map(p => (
        <Paper key={p.id} sx={{ p: 2, mb: 1.5, borderRadius: 2 }} elevation={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>{p.ordem}. {p.pergunta}</Typography>
              {OPCOES.map(letra => (
                <Typography key={letra} variant="body2" sx={{ color: letra === p.resposta_correta ? 'success.main' : 'text.primary', fontWeight: letra === p.resposta_correta ? 700 : 400 }}>
                  {letra.toUpperCase()}) {p[`opcao_${letra}`]}
                </Typography>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="small" onClick={() => abrirEditar(p)}><EditIcon fontSize="small" /></IconButton>
              <IconButton size="small" color="error" onClick={() => deletar(p.id)}><DeleteIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
        </Paper>
      ))}
      {sessaoSel && perguntas.length === 0 && !loading && <Typography variant="body2" color="text.secondary">Nenhuma pergunta cadastrada para esta sessão.</Typography>}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar Pergunta' : 'Nova Pergunta'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Pergunta" value={form.pergunta} onChange={e => setForm(f => ({...f, pergunta: e.target.value}))} fullWidth multiline rows={2} />
          <TextField label="Opção A" value={form.opcao_a} onChange={e => setForm(f => ({...f, opcao_a: e.target.value}))} fullWidth />
          <TextField label="Opção B" value={form.opcao_b} onChange={e => setForm(f => ({...f, opcao_b: e.target.value}))} fullWidth />
          <TextField label="Opção C" value={form.opcao_c} onChange={e => setForm(f => ({...f, opcao_c: e.target.value}))} fullWidth />
          <TextField label="Opção D" value={form.opcao_d} onChange={e => setForm(f => ({...f, opcao_d: e.target.value}))} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Resposta Correta</InputLabel>
            <Select value={form.resposta_correta} label="Resposta Correta" onChange={e => setForm(f => ({...f, resposta_correta: e.target.value}))}>
              {OPCOES.map(l => <MenuItem key={l} value={l}>{l.toUpperCase()}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function CursoDetalhe() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [curso, setCurso] = useState(null);
  const [ministerios, setMinisterios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [tab, setTab] = useState(0);

  const [sessoes, setSessoes] = useState([]);
  const [inscricoes, setInscricoes] = useState([]);
  const [presencas, setPresencas] = useState([]);
  const [materiais, setMateriais] = useState({});
  const [sessaoPresenca, setSessaoPresenca] = useState('');

  const [openSessao, setOpenSessao] = useState(false);
  const [editSessao, setEditSessao] = useState(null);
  const [openMaterial, setOpenMaterial] = useState(null);
  const [editMaterial, setEditMaterial] = useState(null);
  const [openInscricao, setOpenInscricao] = useState(false);
  const [confirmDelSessao, setConfirmDelSessao] = useState(null);
  const [confirmDelMaterial, setConfirmDelMaterial] = useState(null);
  const [confirmDelInscricao, setConfirmDelInscricao] = useState(null);
  const [membros, setMembros] = useState([]);
  const [openAddLider, setOpenAddLider] = useState(false);
  const [membroLider, setMembroLider] = useState('');

  const copiarLinkSessao = (sessaoId) => {
    const url = `http://localhost:3001/meus-cursos?sessao=${sessaoId}`;
    navigator.clipboard.writeText(url);
    alert('Link copiado!');
  };

  const carregarCurso = useCallback(async () => {
    try {
      const [cursoData, ministeriosData] = await Promise.all([
        getCurso(id),
        listarMinisterios()
      ]);
      setCurso(cursoData);
      setMinisterios(ministeriosData);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar curso');
    }
  }, [id]);

  const carregarSessoes = useCallback(async () => {
    try {
      const data = await listarSessoes(id);
      setSessoes(data);
      const mats = {};
      await Promise.all(data.map(async (s) => {
        try {
          const m = await listarMateriais(s.id);
          mats[s.id] = m;
        } catch { mats[s.id] = []; }
      }));
      setMateriais(mats);
    } catch { setSessoes([]); }
  }, [id]);

  const carregarInscricoes = useCallback(async () => {
    try {
      const data = await listarInscricoes(id);
      setInscricoes(data);
    } catch { setInscricoes([]); }
  }, [id]);

  const carregarPresencas = useCallback(async () => {
    if (!sessaoPresenca) { setPresencas([]); return; }
    try {
      const data = await listarPresencas(sessaoPresenca);
      setPresencas(data);
    } catch { setPresencas([]); }
  }, [sessaoPresenca]);

  useEffect(() => {
    setLoading(true);
    Promise.all([carregarCurso(), carregarSessoes(), carregarInscricoes()])
      .finally(() => setLoading(false));
  }, [carregarCurso, carregarSessoes, carregarInscricoes]);

  useEffect(() => { carregarPresencas(); }, [carregarPresencas]);

  const handleCancelarInscricao = async () => {
    if (!confirmDelInscricao) return;
    try {
      await cancelarInscricao(confirmDelInscricao.id);
      setConfirmDelInscricao(null);
      carregarInscricoes();
    } catch {
      alert('Erro ao cancelar inscrição');
      setConfirmDelInscricao(null);
    }
  };

  const handleRegistrarPresencas = async () => {
    if (!sessaoPresenca) return;
    try {
      await registrarPresencas(sessaoPresenca, presencas.map(p => ({
        inscricao_id: p.inscricao_id,
        presente: p.presente
      })));
      alert('Presenças registradas com sucesso!');
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao registrar presenças');
    }
  };

  const togglePresenca = (index) => {
    setPresencas(prev => prev.map((p, i) => i === index ? { ...p, presente: !p.presente } : p));
  };

  const adicionarLiderHandler = async () => {
    if (!membroLider) return;
    try {
      await adicionarLider(id, parseInt(membroLider, 10));
      setOpenAddLider(false);
      setMembroLider('');
      carregarCurso();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao adicionar líder');
    }
  };

  const removerLiderHandler = async (liderId) => {
    try {
      await removerLider(id, liderId);
      carregarCurso();
    } catch {
      alert('Erro ao remover líder');
    }
  };

  const abrirNovaSessao = () => { setEditSessao(null); setOpenSessao(true); };
  const abrirEditarSessao = (s) => { setEditSessao(s); setOpenSessao(true); };

  const baixarCertificado = async (cursoId, inscricaoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://api.conectachurch.com.br'}/cursos/${cursoId}/certificado/${inscricaoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const err = await response.json();
        alert(err.detail || 'Erro ao gerar certificado');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificado_${cursoId}_${inscricaoId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erro ao baixar certificado');
    }
  };

  const baixarRelatorio = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.conectachurch.com.br'}/cursos/${id}/relatorio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `curso_${id}_relatorio.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erro ao baixar relatório');
    }
  };

  const ministroNome = curso?.ministerio_id
    ? (ministerios.find(m => m.id === curso.ministerio_id)?.nome || '—')
    : '—';

  if (loading) {
    return (
      <>
        <Header />
        <ContextNavBar context="cursos" />
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        </Container>
        <Footer />
      </>
    );
  }

  if (erro) {
    return (
      <>
        <Header />
        <ContextNavBar context="cursos" />
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
          <Alert severity="error">{erro}</Alert>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <ContextNavBar context="cursos" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/cursos')} size="small">Voltar</Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 0 } }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{curso.titulo}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip label={STATUS_LABEL[curso.status] || curso.status} color={STATUS_COLOR[curso.status] || 'default'} size="small" />
            <Chip label={ministroNome} variant="outlined" size="small" />
            <Chip label={curso.tipo === 'avulso' ? 'Avulso' : 'Sazonal'} variant="outlined" size="small" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<GetAppIcon />} onClick={baixarRelatorio} size="small">
            Relatório PDF
          </Button>
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/cursos/${id}/editar`)}>
            Editar
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <Tab icon={<InfoIcon />} iconPosition="start" label="Visão Geral" />
        <Tab icon={<EscolaIcon />} iconPosition="start" label="Sessões/Aulas" />
        <Tab icon={<PeopleIcon />} iconPosition="start" label="Inscrições" />
        <Tab icon={<ChecklistIcon />} iconPosition="start" label="Presenças" />
        <Tab icon={<QuizIcon />} iconPosition="start" label="Quiz" />
      </Tabs>

      {/* Aba: Visão Geral */}
      <TabPanel value={tab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Informações do Curso</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Descrição</Typography>
                    <Typography variant="body2">{curso.descricao || 'Nenhuma descrição.'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Ministério</Typography>
                    <Typography variant="body2">{ministroNome}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Local</Typography>
                    <Typography variant="body2">{curso.local || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Data Início</Typography>
                    <Typography variant="body2">{curso.data_inicio ? new Date(curso.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Data Fim</Typography>
                    <Typography variant="body2">{curso.data_fim ? new Date(curso.data_fim + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Vagas Máximas</Typography>
                    <Typography variant="body2">{curso.vagas_maximas || 'Ilimitado'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Carga horária mínima</Typography>
                    <Typography variant="body2">
                      {curso.carga_horaria_minima ? `${curso.carga_horaria_minima} min` : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Inscrição Externa</Typography>
                    <Typography variant="body2">{curso.permitir_inscricao_externo ? 'Permitida' : 'Não permitida'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Líderes</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => {
                    api.get('/secretaria/membros').then(res => setMembros(res.data)).catch(() => {});
                    setOpenAddLider(true);
                  }}>Adicionar</Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(curso.lideres || []).map(l => (
                    <Chip
                      key={l.id}
                      label={l.membro_nome}
                      onDelete={() => removerLiderHandler(l.id)}
                      color="primary"
                      size="small"
                    />
                  ))}
                  {(!curso.lideres || curso.lideres.length === 0) && (
                    <Typography variant="body2" color="text.secondary">Nenhum líder vinculado.</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Aba: Sessões/Aulas */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovaSessao}>Nova Sessão</Button>
        </Box>

        {sessoes.length === 0 ? (
          <Alert severity="info">Nenhuma sessão cadastrada ainda.</Alert>
        ) : (
          sessoes.map((sessao) => (
            <Card key={sessao.id} sx={{ mb: 2, borderRadius: 2 }} elevation={1}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{sessao.titulo}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      {sessao.data && (
                        <Chip label={new Date(sessao.data + 'T00:00:00').toLocaleDateString('pt-BR')} size="small" variant="outlined" />
                      )}
                      {sessao.hora_inicio && (
                        <Chip label={`${sessao.hora_inicio}${sessao.hora_fim ? ` — ${sessao.hora_fim}` : ''}`} size="small" variant="outlined" />
                      )}
                      <Chip label={sessao.tipo} size="small" color={sessao.tipo === 'online' ? 'info' : sessao.tipo === 'hibrido' ? 'warning' : 'default'} />
                      {sessao.ordem != null && <Chip label={`#${sessao.ordem}`} size="small" />}
                    </Box>
                    {sessao.descricao && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{sessao.descricao}</Typography>
                    )}
                    {sessao.local && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Local: {sessao.local}</Typography>
                    )}
                    {sessao.instrutor_nome && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Instrutor: {sessao.instrutor_nome}</Typography>
                    )}
                  </Box>
                   <Box sx={{ display: 'flex' }}>
                    <Tooltip title="Copiar link do aluno">
                      <IconButton size="small" onClick={() => copiarLinkSessao(sessao.id)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Adicionar material">
                      <IconButton size="small" onClick={() => { setEditMaterial(null); setOpenMaterial(sessao.id); }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => abrirEditarSessao(sessao)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton size="small" color="error" onClick={() => setConfirmDelSessao(sessao)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Materiais da sessão */}
                {materiais[sessao.id] && materiais[sessao.id].length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Materiais Didáticos</Typography>
                    {materiais[sessao.id].map(mat => (
                      <Box key={mat.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label={mat.tipo} size="small" variant="outlined" />
                        <Typography variant="body2" sx={{ flex: 1 }}>{mat.titulo}</Typography>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => { setEditMaterial(mat); setOpenMaterial(sessao.id); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover">
                          <IconButton size="small" color="error" onClick={() => setConfirmDelMaterial(mat)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </TabPanel>

      {/* Aba: Inscrições */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenInscricao(true)}>
            Nova Inscrição
          </Button>
        </Box>

        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Data Inscrição</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {inscricoes.map((insc) => (
                <TableRow key={insc.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{insc.nome_inscrito || '—'}</TableCell>
                  <TableCell>
                    <Chip label={insc.externo ? 'Externo' : 'Membro'} size="small" color={insc.externo ? 'warning' : 'primary'} variant="outlined" />
                  </TableCell>
                  <TableCell>{insc.email || '—'}</TableCell>
                  <TableCell>
                    <Chip label={insc.status || 'Ativo'} size="small" color={insc.status === 'cancelado' ? 'error' : 'success'} />
                  </TableCell>
                  <TableCell>
                    {insc.data_inscricao ? new Date(insc.data_inscricao).toLocaleDateString('pt-BR') : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        if (insc.status === 'concluida') {
                          baixarCertificado(curso.id, insc.id);
                        } else {
                          alert('Inscrição não concluída');
                        }
                      }}
                      sx={{ mr: 1 }}
                    >
                      Certificado
                    </Button>
                    {insc.status !== 'cancelado' && (
                      <Tooltip title="Cancelar inscrição">
                        <IconButton size="small" color="error" onClick={() => setConfirmDelInscricao(insc)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {inscricoes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Alert severity="info" sx={{ my: 1 }}>Nenhuma inscrição registrada.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Aba: Presenças */}
      <TabPanel value={tab} index={3}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Sessão</InputLabel>
            <Select value={sessaoPresenca} label="Sessão" onChange={e => setSessaoPresenca(e.target.value)}>
              <MenuItem value="">Selecione uma sessão</MenuItem>
              {sessoes.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.titulo}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {sessaoPresenca && (
            <Button variant="contained" onClick={handleRegistrarPresencas}>
              Salvar Presenças
            </Button>
          )}
        </Box>

        {!sessaoPresenca ? (
          <Alert severity="info">Selecione uma sessão para registrar presenças.</Alert>
        ) : (
          <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Inscrito</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Presente</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {presencas.map((p, index) => (
                  <TableRow key={p.inscricao_id || index} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{p.nome_inscrito || '—'}</TableCell>
                    <TableCell>
                      <Chip label={p.externo ? 'Externo' : 'Membro'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Checkbox checked={!!p.presente} onChange={() => togglePresenca(index)} />
                    </TableCell>
                  </TableRow>
                ))}
                {presencas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Alert severity="info" sx={{ my: 1 }}>Nenhum registro de presença para esta sessão.</Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Aba: Quiz */}
      <TabPanel value={tab} index={4}>
        <TabQuiz cursoId={curso?.id} sessoes={sessoes} />
      </TabPanel>

      {/* Modais */}
      <ModalSessao
        open={openSessao}
        onClose={() => setOpenSessao(false)}
        cursoId={id}
        sessao={editSessao}
        onSuccess={carregarSessoes}
      />

      <ModalMaterial
        open={!!openMaterial}
        onClose={() => { setOpenMaterial(null); setEditMaterial(null); }}
        sessaoId={openMaterial}
        material={editMaterial}
        onSuccess={() => { carregarSessoes(); setOpenMaterial(null); setEditMaterial(null); }}
      />

      <InscricaoModal
        open={openInscricao}
        onClose={() => setOpenInscricao(false)}
        cursoId={id}
        onSuccess={carregarInscricoes}
      />

      {/* Confirmar deletar sessão */}
      <Dialog open={!!confirmDelSessao} onClose={() => setConfirmDelSessao(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deletar Sessão</DialogTitle>
        <DialogContent>
          <Typography>Deletar "{confirmDelSessao?.titulo}"? Materiais vinculados também serão removidos.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelSessao(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={async () => {
            try { await deletarSessao(confirmDelSessao.id); carregarSessoes(); }
            catch { alert('Erro ao deletar sessão'); }
            setConfirmDelSessao(null);
          }}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar deletar material */}
      <Dialog open={!!confirmDelMaterial} onClose={() => setConfirmDelMaterial(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover Material</DialogTitle>
        <DialogContent>
          <Typography>Remover "{confirmDelMaterial?.titulo}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelMaterial(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={async () => {
            try { await deletarMaterial(confirmDelMaterial.id); carregarSessoes(); }
            catch { alert('Erro ao remover material'); }
            setConfirmDelMaterial(null);
          }}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar cancelar inscrição */}
      <Dialog open={!!confirmDelInscricao} onClose={() => setConfirmDelInscricao(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar Inscrição</DialogTitle>
        <DialogContent>
          <Typography>Cancelar inscrição de "{confirmDelInscricao?.nome || confirmDelInscricao?.membro_nome}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelInscricao(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleCancelarInscricao}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog adicionar líder */}
      <Dialog open={openAddLider} onClose={() => setOpenAddLider(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Líder</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Membro</InputLabel>
            <Select value={membroLider} label="Membro" onChange={e => setMembroLider(e.target.value)}>
              <MenuItem value="">Selecione</MenuItem>
              {membros.map(m => (
                <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddLider(false)}>Cancelar</Button>
          <Button onClick={adicionarLiderHandler} variant="contained">Adicionar</Button>
        </DialogActions>
      </Dialog>
    </Container>
      <Footer />
    </>
  );
}

export default CursoDetalhe;
